import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { CheckCheck, ClipboardCheck, ImageIcon, Undo2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, fileUrl, productImage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ImageLightbox from "@/components/ImageLightbox";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import { ReadyCheckSection } from "@/components/ReadyItems";

const RETURN_REASONS = [
  "Quantity does not match evidence",
  "Photo unclear",
  "Wrong product received",
  "Need additional evidence",
];

export default function Approvals() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [returnTarget, setReturnTarget] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [zoomImg, setZoomImg] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/receiving/approvals");
      setSessions(data);
    } catch (e) {
      toast.error(apiError(e));
      setSessions([]);
    }
  }, []);

  useEffect(() => {
    if (["owner", "admin", "admin_input"].includes(user.role)) load();
  }, [user, load]);

  if (!["owner", "admin", "admin_input"].includes(user.role)) {
    return (
      <div data-testid="approvals-forbidden" className="grid place-items-center rounded-md border border-dashed py-20 text-center">
        <p className="font-medium text-muted-foreground">The approval queue is only available to Admin Input / Checker and Owner.</p>
      </div>
    );
  }

  const approve = async (s) => {
    if (confirmId !== s.id) {
      setConfirmId(s.id);
      return;
    }
    setBusy(true);
    try {
      await api.post(`/receiving/sessions/${s.id}/approve`);
      toast.success(`Approved — ${s.batch_name} receiving is now official`);
      setConfirmId(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const submitReturn = async () => {
    if (!reason.trim()) return;
    setBusy(true);
    try {
      await api.post(`/receiving/sessions/${returnTarget.id}/return`, { reason: reason.trim() });
      toast.success("Returned for correction");
      setReturnTarget(null);
      setReason("");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="approvals-page">
      <div className="mb-6">
        <h1 className="font-heading text-2xl font-extrabold tracking-tight">Checker Approval</h1>
        <p className="text-sm text-muted-foreground">Receiving sessions waiting for review — nothing becomes official until approved</p>
      </div>

      <ReadyCheckSection />

      {sessions === null ? (
        <div className="grid place-items-center py-20">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        </div>
      ) : sessions.length === 0 ? (
        <div data-testid="approvals-empty" className="grid place-items-center rounded-md border border-dashed py-20 text-center">
          <div>
            <ClipboardCheck className="mx-auto mb-3 h-10 w-10 text-stone-300" />
            <p className="font-medium text-muted-foreground">No sessions waiting for approval</p>
          </div>
        </div>
      ) : (
        <div className="space-y-3" data-testid="approvals-list">
          {sessions.map((s) => {
            const entered = s.items.filter((it) => it.actual_qty > 0);
            const totalUnits = entered.reduce((sum, it) => sum + it.actual_qty, 0);
            const selfBlocked = user.role !== "owner" && (s.pic_id === user.id || s.submitted_by_id === user.id);
            return (
              <div key={s.id} data-testid={`approval-card-${s.id}`} className="space-y-3 rounded-md border bg-card p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <p className="font-heading text-base font-bold tracking-tight">{s.batch_name}</p>
                    <p className="text-xs text-muted-foreground">
                      #{s.id.slice(0, 8)} · {s.pic_name} · submitted{" "}
                      {s.submitted_at ? format(new Date(s.submitted_at), "d MMM yyyy, HH:mm") : "—"}
                    </p>
                  </div>
                  <span className="rounded-md border border-rose-200 bg-rose-50 px-2 py-1 text-xs font-bold text-rose-700">
                    {totalUnits} units · PENDING
                  </span>
                </div>
                <div className="space-y-1.5">
                  {entered.map((it) => (
                    <div key={it.product_id} className="space-y-2 rounded-md border bg-[#EDE4DE] px-3 py-2">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex min-w-0 items-center gap-2.5">
                          {productImage(it) ? (
                            <img src={productImage(it)} alt={it.product_name} className="h-11 w-11 shrink-0 rounded-md border bg-white object-cover" />
                          ) : (
                            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md border bg-white">
                              <ImageIcon className="h-4 w-4 text-stone-300" />
                            </div>
                          )}
                          <div className="min-w-0">
                            <p className="truncate text-sm font-semibold">{it.product_name}</p>
                            <p className="text-xs text-muted-foreground">
                              Purchased {it.purchased_qty} · Prev received {it.previously_received_qty} · This session{" "}
                              <b className="text-stone-700">{it.actual_qty}</b> · Remaining {it.remaining_after}
                            </p>
                          </div>
                        </div>
                        <span
                          className={`shrink-0 rounded-md border px-2 py-0.5 text-xs font-bold ${
                            it.result === "FULL"
                              ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                              : "border-amber-200 bg-amber-50 text-amber-700"
                          }`}
                        >
                          {it.result}
                        </span>
                      </div>
                      <div className="flex flex-wrap items-center gap-1.5" data-testid={`approval-evidence-${it.product_id}`}>
                        <span className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">Evidence:</span>
                        {(it.evidence || []).map((ev) => (
                          <button
                            key={ev.id}
                            type="button"
                            data-testid={`approval-evidence-thumb-${ev.id}`}
                            aria-label="View evidence photo"
                            onClick={() => setZoomImg({ src: fileUrl(ev.path), alt: `${it.product_name} evidence` })}
                          >
                            <img src={fileUrl(ev.path)} alt="Receiving evidence" className="h-11 w-11 rounded-md border bg-white object-cover" />
                          </button>
                        ))}
                        {!(it.evidence || []).length && <span className="text-xs font-semibold text-red-600">No photos</span>}
                      </div>
                    </div>
                  ))}
                </div>
                {s.note && (
                  <p className="rounded-md border bg-accent/40 px-3 py-2 text-xs text-stone-600" data-testid={`approval-note-${s.id}`}>
                    <b>Note:</b> {s.note}
                  </p>
                )}
                {selfBlocked && (
                  <p data-testid={`approval-self-blocked-${s.id}`} className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs font-semibold text-red-700">
                    You created or submitted this session — approval requires a different Checker or Owner Override.
                  </p>
                )}
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    data-testid={`approval-review-${s.id}`}
                    className="h-11 flex-1"
                    onClick={() => navigate(`/receiving/sessions/${s.id}`)}
                  >
                    Review
                  </Button>
                  <Button
                    variant="outline"
                    data-testid={`approval-return-${s.id}`}
                    className="h-11"
                    onClick={() => {
                      setReturnTarget(s);
                      setReason("");
                    }}
                  >
                    <Undo2 className="h-4 w-4" /> Return
                  </Button>
                  <Button
                    data-testid={`approval-approve-${s.id}`}
                    className={`h-11 flex-1 ${confirmId === s.id ? "" : "bg-emerald-600 hover:bg-emerald-700"}`}
                    onClick={() => approve(s)}
                    disabled={busy || selfBlocked}
                  >
                    <CheckCheck className="h-4 w-4" /> {confirmId === s.id ? "Confirm Approve" : "Approve"}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Drawer open={!!returnTarget} onOpenChange={(o) => !o && setReturnTarget(null)} shouldScaleBackground={false}>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle className="font-heading">Return for Correction — {returnTarget?.batch_name}</DrawerTitle>
          </DrawerHeader>
          <div className="space-y-4 px-4 pb-8">
            <p className="text-sm text-muted-foreground">
              The Receiving PIC can edit quantities and evidence again. Official Received stays unchanged. Your reason is recorded in the audit history.
            </p>
            <div className="flex flex-wrap gap-2">
              {RETURN_REASONS.map((r) => (
                <button
                  key={r}
                  type="button"
                  data-testid={`approval-reason-chip-${r.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  onClick={() => setReason(r)}
                  className={`h-9 rounded-full border px-3 text-xs font-semibold transition-colors active:scale-[0.98] ${
                    reason === r ? "border-red-500 bg-red-500 text-white" : "bg-card text-stone-600 hover:bg-accent"
                  }`}
                >
                  {r}
                </button>
              ))}
            </div>
            <Textarea
              data-testid="approval-return-reason-input"
              placeholder="Reason (required)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
            <Button
              variant="destructive"
              data-testid="approval-return-confirm-button"
              className="h-12 w-full text-base"
              onClick={submitReturn}
              disabled={busy || !reason.trim()}
            >
              Confirm Return
            </Button>
          </div>
        </DrawerContent>
      </Drawer>

      {zoomImg && <ImageLightbox src={zoomImg.src} alt={zoomImg.alt} onClose={() => setZoomImg(null)} />}
    </div>
  );
}
